import { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useSession } from 'next-auth/react'

import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { getAllInfo } from '#/entities/user-account/model/user-type-slice'
import { commaSeparated, Modal, TooltipCustom } from '#/shared'
import { accountApi } from '#/shared/api/account-endpoints'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import { balanceSelector } from '#/shared/lib/selectors'

import { IFeatures, IOffer } from '../api'

import { CurrentPlanAndBalance } from './current-plan-and-balance'
import { IndividualSubscriptionNotice } from './individual-subscription-notice'
import { FaqSection } from './faq-section'
import { FeaturesSection } from './features-section'
import { PlansSection } from './plans-section'

import styles from './subscription.module.scss'

export const SubscriptionRecurrentPage = () => {
	const [offers, setOffers] = useState<IOffer[] | null>(null)
	const [currentTokenLimitUID, setCurrentTokenLimitUID] = useState<string>('')
	const [tokenLimits, setTokenLimits] = useState<Array<{ value: string; label: string }>>([])
	const [currentFeatures, setCurrentFeatures] = useState<IFeatures[]>([])
	const {
		uid: paymentPlanUid,
		tokens_per_plan: planTokenLimit,
		price: planPrice,
		individual: isIndividual,
	} = useAppSelector((state) => state.user.payment_plan.plan)
	const nextPaymentAt = useAppSelector((state) => state.user.payment_plan.next_payment_at)
	const isRecurring = useAppSelector((state) => state.user.payment_plan?.is_recurring) ?? false
	const balance = useAppSelector(balanceSelector)
	const dispatch = useAppDispatch()
	const { data } = useSession()
	const { showMessage } = useShowDataStore()
	const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] = useState(false)
	const isDemoPlan = Number(planPrice) <= 0

	const handleOpenCancelSubscriptionModal = () => setIsCancelSubscriptionModalOpen(true)
	const handleCloseCancelSubscriptionModal = () => setIsCancelSubscriptionModalOpen(false)

	const handleCancelSubscription = async () => {
		if (!data?.access) {
			showMessage(`Не удалось отменить подписку: не удалось определить сессию`)
			return
		}
		try {
			await accountApi.revokeRecurringPayment(data.access)
			await dispatch(getAllInfo(data.access)).unwrap()
			showMessage('Автопродление подписки отменено', 'success')
			handleCloseCancelSubscriptionModal()
		} catch (e: unknown) {
			const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
			const errMsg =
				detail ?? (e instanceof Error ? e.message : typeof e === 'string' ? e : 'Неизвестная ошибка')
			showMessage(`Не удалось отменить подписку: ${errMsg}`)
		}
	}

	useEffect(() => {
		if (data?.access) {
			accountApi.getPaymentsPlans(data?.access).then((res) => {
				if (res) {
					setOffers(res)
					setCurrentTokenLimitUID(res[0].uid)
					setTokenLimits(
						res.map((offer) => ({
							value: offer.uid,
							label: `${commaSeparated(Math.round(Number(offer.tokens_per_plan)))} токенов`,
						}))
					)
				}
			})
		}
	}, [data?.access])

	useEffect(() => {
		if (offers && currentTokenLimitUID) {
			const foundOffer = offers.find((offer) => offer.uid === currentTokenLimitUID)
			if (foundOffer) {
				setCurrentFeatures(foundOffer.grouped_features)
			}
		}
	}, [currentTokenLimitUID, offers])

	const planTokensFormatted = commaSeparated(Math.round(Number(planTokenLimit) || 0))

	if (isIndividual) {
		return (
			<Box className={styles.container}>
				<Box sx={{ marginTop: '20px', marginBottom: '20px' }}>
					<Typography sx={{ fontSize: 35, fontWeight: '600' }}>Подписка</Typography>
				</Box>

				<CurrentPlanAndBalance
					balance={balance}
					planPrice={planPrice}
					planTokenLimit={planTokenLimit}
					isIndividual={isIndividual}
					isRecurring={isRecurring}
					nextPaymentAt={nextPaymentAt}
				/>

				<IndividualSubscriptionNotice />

				<FaqSection />
			</Box>
		)
	}

	return (
		<Box className={styles.container}>
			<Box sx={{ marginTop: '20px', marginBottom: '20px' }}>
				<Typography sx={{ fontSize: 35, fontWeight: '600' }}>Подписка</Typography>
			</Box>

			<CurrentPlanAndBalance
				balance={balance}
				planPrice={planPrice}
				planTokenLimit={planTokenLimit}
				isIndividual={isIndividual}
				isRecurring={isRecurring}
				nextPaymentAt={nextPaymentAt}
			/>

			<PlansSection offers={offers} currentOfferUID={paymentPlanUid} currentPlanTokens={Number(planTokenLimit)} />

			<FeaturesSection
				tokenLimits={tokenLimits}
				currentTokenLimitUID={currentTokenLimitUID}
				onCurrentTokenLimitUIDChange={setCurrentTokenLimitUID}
				features={currentFeatures}
			/>

			<FaqSection />

			{!isDemoPlan && !isIndividual ? (
				<Box className={styles.cancelSubscriptionButtonWrapper}>
					{!isRecurring ? (
						<TooltipCustom
							title={'Подписка уже была отменена'}
							placement="top"
						>
							<span className={styles.cancelSubscriptionButtonTooltipTarget}>
								<Button
									variant="outlined"
									className={styles.cancelSubscriptionButton}
									fullWidth
									disabled
									onClick={handleOpenCancelSubscriptionModal}
								>
									Отмена подписки
								</Button>
							</span>
						</TooltipCustom>
					) : (
						<Button
							variant="outlined"
							className={styles.cancelSubscriptionButton}
							fullWidth
							onClick={handleOpenCancelSubscriptionModal}
						>
							Отмена подписки
						</Button>
					)}
				</Box>
			) : null}

			<Modal
				open={isCancelSubscriptionModalOpen}
				onClose={handleCloseCancelSubscriptionModal}
				paperSx={{ backgroundColor: '#151518' }}
			>
				<Box className={styles.cancelSubscriptionModalContainer}>
					<Typography className={styles.cancelSubscriptionModalTitle}>Отмена подписки</Typography>

					<Typography className={styles.cancelSubscriptionModalText} component="p">
						Внимание! Вы отменяете подписку на тариф{' '}
						<Typography component="span" className={styles.cancelSubscriptionModalTextEmphasis}>
							{planTokensFormatted} токенов
						</Typography>
						. Следующее списание будет отменено, вы можете продолжать пользоваться возможностями тарифа до истечения
						его срока.
					</Typography>

					<Box className={styles.cancelSubscriptionModalButtons}>
						<Button onClick={handleCloseCancelSubscriptionModal} fullWidth className={styles.cancelSubscriptionButtonKeep}>
							Не отменять
						</Button>
						<Button
							onClick={() => void handleCancelSubscription()}
							fullWidth
							variant="outlined"
							className={styles.cancelSubscriptionButtonCancel}
						>
							Да, отменить
						</Button>
					</Box>
				</Box>
			</Modal>
		</Box>
	)
}
